import { useEffect, useMemo, useState } from "react";
import type {
  OnboardingOccasion,
  OnboardingTourId,
  TourCapability,
} from "@playbookai/types";
import { onOnboardingOccasion } from "@playbookai/ui";
import type { OnboardingTours } from "./useOnboardingTours.js";

/**
 * Occasion → tour. ONE place, so a feature package cannot pick its own tour and
 * the mapping is reviewable as a table.
 *
 * `solve-opened-as-owner` deliberately does NOT complete or suppress
 * `solve-collaborator`: the two are independent tours about different
 * situations, and somebody who owns one SOLVE may join another as a
 * collaborator next week.
 */
const OCCASION_TOURS: Record<OnboardingOccasion, OnboardingTourId> = {
  "solve-opened-as-owner": "solve-owner",
  "solve-opened-as-collaborator": "solve-collaborator",
  "solve-invitation-sent": "solve-inviter",
  "vsm-opened": "vsm",
  "hoshin-opened": "hoshin",
};

export interface TourTriggerInput {
  readonly tours: OnboardingTours;
  /** True while the user is on the Home route. */
  readonly onHome: boolean;
  /** Shell-level capabilities, merged with whatever an occasion supplies. */
  readonly shellCapabilities: readonly TourCapability[];
}

export interface TourTriggerResult {
  /** The capability set the host resolves steps against. */
  readonly capabilities: ReadonlySet<TourCapability>;
}

/**
 * Decide which tour, if any, should be running.
 *
 * Every start goes through `tours.request`, which refuses while another tour is
 * on screen — so two eligible triggers never overlap, and the later one is
 * simply re-evaluated on the next render after the first ends.
 */
export function useOnboardingTourTriggers({
  tours,
  onHome,
  shellCapabilities,
}: TourTriggerInput): TourTriggerResult {
  // The capabilities the LAST occasion reported. Held in shell state because
  // the workspace that knows them is a different React tree from the overlay
  // that needs them.
  const [pendingTour, setPendingTour] = useState<OnboardingTourId | null>(null);
  const [occasionCapabilities, setOccasionCapabilities] = useState<
    readonly TourCapability[]
  >([]);

  useEffect(
    () =>
      // Subscribed through the design system's own helper, so the listener and
      // every feature package's dispatcher agree on the event name and the
      // payload cast by construction.
      onOnboardingOccasion((detail) => {
        const tourId = OCCASION_TOURS[detail.occasion as OnboardingOccasion];
        if (!tourId) return;
        setOccasionCapabilities(detail.capabilities ?? []);
        // `eligible` is false while the progress read is loading OR failed, so
        // a tour can never start on the strength of a read that did not answer.
        setPendingTour(tourId);
      }),
    [tours],
  );

  useEffect(() => {
    if (!pendingTour || !tours.ready || tours.active !== null) return;
    if (tours.eligible(pendingTour)) tours.request(pendingTour);
    setPendingTour(null);
  }, [pendingTour, tours]);

  useEffect(() => {
    // Home has no event to wait for: being on the page IS the occasion. It is
    // still gated on `eligible`, so a direct link into a workspace never gets
    // redirected here to force it.
    if (!onHome) return;
    if (tours.eligible("home")) tours.request("home");
  }, [onHome, tours]);

  const capabilities = useMemo(
    () =>
      new Set<TourCapability>([...shellCapabilities, ...occasionCapabilities]),
    [shellCapabilities, occasionCapabilities],
  );
  return { capabilities };
}
